import React, { useState, useEffect } from "react";
import {
    Dialog, DialogHeader, DialogBody, DialogFooter,
    Typography, Button, IconButton
} from "@material-tailwind/react";
import { CheckCircleIcon, ForwardIcon, XMarkIcon } from "@heroicons/react/24/outline";
import axios from "axios";

export default function ProcessingDialog({ isOpen, onClose, onSuccess, requestHeaders, shipmentData }) {

    const [processingQueue, setProcessingQueue] = useState([]);
    const [processingIndex, setProcessingIndex] = useState(0);
    const [processingCandidates, setProcessingCandidates] = useState([]);
    const [processingCandidatesLoading, setProcessingCandidatesLoading] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [skippedItems, setSkippedItems] = useState([]);

    // Kad se dialogs otvori sa novim shipmentData, startuj queue
    useEffect(() => {
        if (isOpen && shipmentData && shipmentData.items.length > 0) {
            setProcessingQueue(shipmentData.items);
            setProcessingIndex(0);
            setSkippedItems([]);
            fetchCandidatesForItem(shipmentData.items[0].title);
        }
    }, [isOpen, shipmentData]);

    const fetchCandidatesForItem = async (title) => {
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";
        setProcessingCandidatesLoading(true);
        setSelectedCandidate(null);
        try {
            const params = new URLSearchParams({
                page: '0', size: '50', searchTerm: title, searchType: 'title',
                statuses: 'DISPATCHED,DISPATCHED_PERSONAL', sortField: 'purchasedDate', sortDirection: 'asc'
            });
            const response = await axios.get(
                `${API_URL}/purchased_items/all?${params.toString()}`,
                { headers: requestHeaders }
            );
            setProcessingCandidates(response.data.content);
        } catch (err) {
            console.error('Error fetching candidates:', err);
            setProcessingCandidates([]);
        } finally {
            setProcessingCandidatesLoading(false);
        }
    };

    const goToNextItem = () => {
        const nextIndex = processingIndex + 1;
        if (nextIndex >= processingQueue.length) {
            // Gotovi smo
            handleClose();
            return;
        }
        setProcessingIndex(nextIndex);
        fetchCandidatesForItem(processingQueue[nextIndex].title);
    };

    const confirmProcessingItem = async () => {
        if (!selectedCandidate) return;
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";
        const currentItem = processingQueue[processingIndex];

        const now = new Date();
        const offset = now.getTimezoneOffset() * 60000;
        const localTime = new Date(now - offset);
        const availabilityDate = localTime.toISOString().slice(0, 19);

        try {
            await axios.put(
                `${API_URL}/purchased_items/edit/${selectedCandidate.id}`,
                {
                    status: 'IN_STOCK', availabilityDate,
                    shippingFees: currentItem.shippingFees,
                    parcelForwardingFees: currentItem.parcelForwardingFees,
                    royalMailOrderNumber: shipmentData.royalMailOrderNumber,
                    yugotransportInternalNumber: shipmentData.yugotransportInternalNumber,
                    yugotransportOrderNumber: shipmentData.yugotransportOrderNumber,
                },
                { headers: requestHeaders }
            );
            goToNextItem();
        } catch (err) {
            console.error('Error updating item:', err);
            alert('Error updating item. Please try again.');
        }
    };

    const handleClose = () => {
        setProcessingQueue([]);
        setProcessingIndex(0);
        setProcessingCandidates([]);
        setSelectedCandidate(null);
        setSkippedItems([]);
        onSuccess();
        onClose();
    };

    return (
        <Dialog size="md" open={isOpen} handler={() => {}}
                className="p-4 mt-10 mb-10 flex flex-col max-h-[80vh] overflow-hidden">
            <DialogHeader className="relative m-0 block">
                <div className="flex items-center justify-between pr-12">
                    <div>
                        <Typography variant="h4" color="blue-gray">Processing Shipment</Typography>
                        <Typography className="mt-1 font-normal text-gray-600">
                            Item {processingIndex + 1} of {processingQueue.length}
                        </Typography>
                    </div>
                    <div className="w-32">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-cyan-900 dark:bg-cyan-200 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${processingQueue.length > 0 ? ((processingIndex) / processingQueue.length) * 100 : 0}%` }}></div>
                        </div>
                        <Typography variant="small" className="text-gray-500 text-right mt-1">
                            {processingQueue.length > 0 ? Math.round((processingIndex / processingQueue.length) * 100) : 0}%
                        </Typography>
                    </div>
                </div>
                <IconButton size="sm" variant="text" className="!absolute right-3.5 top-3.5" onClick={handleClose}>
                    <XMarkIcon className="h-4 w-4 stroke-2" />
                </IconButton>
            </DialogHeader>

            <DialogBody className="overflow-y-auto flex-grow">
                {processingQueue.length > 0 && processingIndex < processingQueue.length && (
                    <div className="space-y-4">
                        {/* Current item info */}
                        <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg p-3">
                            <Typography variant="h6" className="text-cyan-900 dark:text-cyan-200 font-bold">
                                {processingQueue[processingIndex].title}
                            </Typography>
                            <div className="flex gap-4 mt-1 text-sm text-cyan-800 dark:text-cyan-300">
                                <span>Weight: {processingQueue[processingIndex].weightG}g</span>
                                <span>Shipping: £{processingQueue[processingIndex].shippingFees}</span>
                                <span>Parcel: £{processingQueue[processingIndex].parcelForwardingFees}</span>
                            </div>
                        </div>

                        {/* Candidates */}
                        {processingCandidatesLoading ? (
                            <div className="flex justify-center py-6">
                                <div className="flex flex-col items-center gap-2">
                                    <div className="w-8 h-8 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin"></div>
                                    <Typography variant="small" className="text-gray-500">Searching...</Typography>
                                </div>
                            </div>
                        ) : processingCandidates.length === 0 ? (
                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-center">
                                <Typography variant="h6" className="text-red-700 dark:text-red-300">No DISPATCHED items found</Typography>
                                <Typography variant="small" className="text-red-500 dark:text-red-400 mt-1">
                                    No dispatched item found with this title in the database.
                                </Typography>
                            </div>
                        ) : (
                            <div>
                                <Typography variant="small" color="blue-gray" className="font-bold mb-2">
                                    {processingCandidates.length === 1
                                        ? 'Found 1 dispatched item — confirm to update:'
                                        : `Found ${processingCandidates.length} dispatched items — select one:`}
                                </Typography>
                                <div className="space-y-2">
                                    {processingCandidates.map((candidate) => (
                                        <div
                                            key={candidate.id}
                                            onClick={() => setSelectedCandidate(candidate)}
                                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                                selectedCandidate?.id === candidate.id
                                                    ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/30'
                                                    : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-dark-700'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                                    selectedCandidate?.id === candidate.id
                                                        ? 'border-cyan-500 bg-cyan-500'
                                                        : 'border-gray-400'
                                                }`}>
                                                    {selectedCandidate?.id === candidate.id && (
                                                        <div className="w-2 h-2 rounded-full bg-white"></div>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <Typography variant="small" className="font-medium dark:text-gray-300">
                                                            {candidate.title}
                                                        </Typography>
                                                        {candidate.status === 'DISPATCHED_PERSONAL' && (
                                                            <span className="text-[10px] font-semibold uppercase tracking-wide text-purple-500 dark:text-purple-400 border border-purple-300 dark:border-purple-600 rounded px-1.5 py-0.5 leading-none">
                                                                personal
                                                            </span>
                                                        )}
                                                    </div>
                                                    <Typography variant="small" className="text-gray-500 dark:text-gray-400">
                                                        ID: {candidate.id} • Purchased: {new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(candidate.purchasedDate))}
                                                        {candidate.wobOrderNumber && ` • WOB: ${candidate.wobOrderNumber}`}
                                                    </Typography>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </DialogBody>

            <DialogFooter className="flex justify-between flex-shrink-0">
                <Button
                    onClick={() => {
                        setSkippedItems(prev => [...prev, processingQueue[processingIndex].title]);
                        goToNextItem();
                    }}
                    variant="outlined"
                    className="mv-secondary-button"
                >
                    <ForwardIcon className="h-4 w-4" />
                    Skip
                </Button>
                <Button onClick={confirmProcessingItem} disabled={!selectedCandidate} className="mv-primary-button">
                    <CheckCircleIcon className="h-4 w-4" />
                    Confirm & Next
                </Button>
            </DialogFooter>
        </Dialog>
    );
}
